import {observer} from 'mobx-react';
import React from 'react';
import {X4Unit} from '../../../domain';
import {DisplayError, Link, PageHeading, PromiseMessage} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {AddUnitControls} from './parts/AddUnitControls';
import {AddUnitForm} from './parts/AddUnitForm';
import {DeleteUnitControls} from './parts/DeleteUnitControls';
import {EditUnitControls} from './parts/EditUnitControls';
import {EditUnitForm} from './parts/EditUnitForm';
import {MoveUnitControls} from './parts/MoveUnitControls';
import {MoveUnitForm} from './parts/MoveUnitForm';
import {ReorderUnitsControls} from './parts/ReorderUnitsControls';
import {X3LevelBreadcrumbs} from './X3LevelBreadcrumbs';
import {X3LevelStore} from './X3LevelStore';
import {userStatusService} from '../../../domain'

function UnitViewNoActions(unit: X4Unit): React.ReactNode {
  return (
    <div className="row" key={'unit-' + unit.Id}>
      <div className="col">
        <div className="text-box">
          {unit.Name}
        </div>
      </div>
    </div>
  );
}

function* ViewModeItems(store: X3LevelStore): ReactNodeGenerator {
  const units = store.loader.units;
  if (units.length === 0)
    yield (
      <div className="row justify-content-center" key="no-units">
        <div className="col-auto">
          <div className="alert alert-info">
            No units
          </div>
        </div>
      </div>);

  for (const unit of units) {
    yield (
      <div className="row" key={'unit-' + unit.Id}>
        <div className="col">
          <div className="text-box">
            {unit.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <Link className="btn btn-primary" to={routes.x4unit(unit.Id)}> View </Link>
          {
            userStatusService.status.roles.ContentManager &&
            <>
              <button type="button" className="btn btn-primary"
                      onClick={() => store.beginEdit(unit.Id)}> Edit
              </button>
              <button type="button" className="btn btn-primary"
                      onClick={() => store.beginMove(unit.Id)}> Move
              </button>
              <button type="button" className="btn btn-danger"
                      onClick={() => store.beginDelete(unit.Id)}> Delete
              </button>
            </>
          }
        </div>
      </div>);
  }
}

function* AddModeItems(store: X3LevelStore): ReactNodeGenerator {
  const units = store.loader.units;

  for (const unit of units)
    yield UnitViewNoActions(unit);

  yield (<AddUnitForm key="add-unit-form"
                      store={store}/>);

  yield (<AddUnitControls key="add-unit-controls"
                          store={store}/>);
}

function* EditModeItems(store: X3LevelStore): ReactNodeGenerator {
  const units = store.loader.units;
  const editUnitId = store.editUnit!.unit.Id;

  for (const unit of units) {
    if (unit.Id !== editUnitId) {
      yield UnitViewNoActions(unit);
      continue;
    }

    yield (<EditUnitForm key="edit-unit-form"
                         store={store}/>);

    yield (<EditUnitControls key="edit-unit-controls"
                             store={store}/>);
  }
}

function* MoveModeItems(store: X3LevelStore): ReactNodeGenerator {
  const units = store.loader.units;
  const moveUnitId = store.move!.unit.Id;

  for (const unit of units) {
    yield UnitViewNoActions(unit);

    if (unit.Id !== moveUnitId)
      continue;

    yield (<MoveUnitForm key="move-unit-form"
                         store={store}/>);

    yield (<MoveUnitControls key="move-unit-controls"
                             store={store}/>);
  }
}

function* DeleteModeItems(store: X3LevelStore): ReactNodeGenerator {
  const units = store.loader.units;
  const deleteUnitId = store.deleteUnit!.unitId;

  for (let unit of units) {
    yield UnitViewNoActions(unit);

    if (unit.Id !== deleteUnitId)
      continue;

    yield (<DeleteUnitControls key="delete-unit-controls"
                               store={store}/>);
  }
}

function* ReorderModeItems(store: X3LevelStore): ReactNodeGenerator {
  const unitIds = store.reorder!.unitIds;
  const unitById = store.loader.unitById;

  for (let index = 0; index < unitIds.length; index++) {
    let unitId = unitIds[index];
    const unit = unitById[unitId];
    const first = index === 0;
    const last = index === unitIds.length - 1;

    yield (
      <div className="row" key={'unit-' + unit.Id}>
        <div className="col">
          <div className="text-box">
            {unit.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderUp(index)}
                  disabled={first}>
            <span className="fas fa-arrow-up"/>
          </button>
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderDown(index)}
                  disabled={last}>
            <span className="fas fa-arrow-down"/>
          </button>
        </div>
      </div>);
  }

  yield (<ReorderUnitsControls key="reorder-units-controls"
                               store={store}/>);
}

function* cardBodyRenderer(store: X3LevelStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'View':
      yield* ViewModeItems(store);
      break;
    case 'Edit':
      yield* EditModeItems(store);
      break;
    case 'Add':
      yield* AddModeItems(store);
      break;
    case 'Delete':
      yield* DeleteModeItems(store);
      break;
    case 'ReOrder':
      yield* ReorderModeItems(store);
      break;
    case 'Move':
      yield* MoveModeItems(store);
      break;
  }
}

function* sceneRenderer(store: X3LevelStore): ReactNodeGenerator {
  const loaderState = store.loader.state;
  const isPending = loaderState?.isPending === true;
  const isFailure = loaderState?.isRejected === true;
  const isSuccess = loaderState?.isResolved === true;
  const level = store.loader.level;

  if (isPending || isFailure) {
    yield (<PageHeading key="heading"> Level </PageHeading>);

    yield (<PromiseMessage key="lookup-loader"
                           label="Loading level"
                           state={loaderState}
                           align="right"/>);

    if (isFailure)
      yield (
        <div className="row justify-content-center" key="lookup-error">
          <div className="col-auto">
            <button type="button" className="btn btn-primary"
                    onClick={store.loader.load}>
              Retry
            </button>
          </div>
        </div>
      );
    return;
  }

  if (!isSuccess)
    return;

  if (level === null) {
    yield (<DisplayError key="age-category-not-found">
      Level with Id {store.levelId} not found
    </DisplayError>);
    return;
  }

  yield (<X3LevelBreadcrumbs key="level-breadcrumbs" level={level}/>);

  yield (<PageHeading key="heading"> Level {level!.Name} </PageHeading>);


  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col"> Units</div>
              {
                store.mode === 'View' &&
                <div className="col-auto no-wrap">
                  {
                    userStatusService.status.roles.ContentManager &&
                    <>
                      <button type="button" className="btn btn-primary"
                              onClick={store.beginAdd}> Add
                      </button>
                      <button type="button" className="btn btn-primary"
                              disabled={store.loader.units.length < 2}
                              onClick={store.beginReorder}> Reorder
                      </button>
                    </>
                  }
                </div>
              }
            </div>
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(store))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  id: number;
}

@observer
export class X3LevelComponent extends React.Component<Props> {
  readonly store: X3LevelStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new X3LevelStore(props.id);
  }

  componentDidMount() {
    this.store.loader.load();
  }

  render() {
    return Array.from(sceneRenderer(this.store));
  }

  componentWillUnmount(): void {
    this.store.dispose();
  }
}
